import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { growthPlans, growthPlanComparison, formatPlanPrice } from '../data/growthPlans'
import { duration, ease, stagger } from '../lib/motion'

function scrollToInquiry() {
  document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' })
}

// BWC Care Plans section (component kept as GrowthPlans.jsx / imported as
// GrowthPlans to minimize churn across AppRoutes.jsx/GetAWebsite.jsx —
// the visible copy, section id, and data are what actually changed here;
// see data/growthPlans.js for the full rationale on the rename).
//
// Deliberately not a copy of Pricing's card treatment: no elevated shadow-card
// grid, no "featured" glow border — just an accent-colored badge on the
// recommended plan and a plain divider list. The goal is for this to read
// as "the next chapter," not "a second set of packages to compare against
// Launch/Growth/Scale.
//
// The comparison table below follows the same logic: it exists, but it's
// collapsed by default and opened on request, not front-and-center. Someone
// deciding between three sequential monthly-partnership tiers wants "what's
// the actual difference" available, not a spec sheet competing for
// attention with the framing copy above it.
export default function GrowthPlans({ onSelect }) {
  const [cycle, setCycle] = useState('monthly')
  const [showComparison, setShowComparison] = useState(false)

  return (
    <section
      id="care-plans"
      className="py-24 border-t"
      style={{ borderColor: 'var(--border)', background: 'var(--bg-elevated)' }}
    >
      <div className="container-bw">
        <div className="max-w-xl mb-10">
          <p className="eyebrow mb-4">BWC Care Plans</p>
          <h2 className="font-display text-3xl md:text-4xl mb-4" style={{ color: 'var(--text-primary)' }}>
            Your website is built. Now it needs upkeep.
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Whether we built your site or someone else did, a Care Plan keeps it accurate,
            secure, and working, every visitor, every month, regardless of how you got your
            website. It's optional, it starts anytime, and every plan below is priced the
            same no matter which build path you took.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-10">
          <div
            role="radiogroup"
            aria-label="Billing cycle"
            className="inline-flex items-center rounded-full p-1 border"
            style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
          >
            {['monthly', 'annual'].map((mode) => {
              const active = cycle === mode
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCycle(mode)}
                  className="relative px-3.5 py-1.5 rounded-full text-xs font-medium capitalize focus-visible:outline-none"
                  style={{
                    color: active ? 'var(--accent-text)' : 'var(--text-secondary)',
                    background: active ? 'var(--accent)' : 'transparent',
                    transition: 'background-color 280ms ease, color 280ms ease',
                  }}
                >
                  {mode}
                </button>
              )
            })}
          </div>
          {cycle === 'annual' && (
            <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>
              Save 15%, billed yearly
            </span>
          )}
        </div>

        <div className="flex flex-col divide-y" style={{ borderColor: 'var(--border)' }}>
          {growthPlans.map((plan, i) => {
            const { price, usdPrice, billedNote } = formatPlanPrice(plan, cycle)
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: duration.base, delay: i * stagger.tight }}
                className="grid grid-cols-1 md:grid-cols-[1.2fr_1.6fr_auto] gap-6 md:gap-10 py-8 items-start"
                style={{ borderColor: 'var(--border)' }}
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-display text-xl" style={{ color: 'var(--text-primary)' }}>
                      {plan.name}
                    </h3>
                    {plan.badge && (
                      <span
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                        style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
                      >
                        {plan.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                    {plan.for}
                  </p>
                  <p className="text-sm font-mono" style={{ color: 'var(--accent)' }}>
                    {price}
                    {usdPrice && <span style={{ color: 'var(--text-muted)' }}> · {usdPrice}</span>}
                  </p>
                  {billedNote && (
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      {billedNote}
                    </p>
                  )}
                </div>

                <ul className="flex flex-col gap-1.5">
                  {plan.features.map((f) => (
                    <li key={f} className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {f}
                    </li>
                  ))}
                  {plan.note && (
                    <li className="text-xs mt-1.5 italic" style={{ color: 'var(--text-muted)' }}>
                      {plan.note}
                    </li>
                  )}
                </ul>

                <div className="md:pt-1">
                  <button
                    onClick={() => {
                      onSelect(plan.id)
                      scrollToInquiry()
                    }}
                    className="text-sm font-medium px-5 py-2.5 rounded-lg border whitespace-nowrap"
                    style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                  >
                    {plan.cta}
                  </button>
                </div>
              </motion.div>
            )
          })}
        </div>

        <div className="mt-10">
          <button
            type="button"
            onClick={() => setShowComparison((v) => !v)}
            aria-expanded={showComparison}
            className="text-sm font-medium underline underline-offset-4"
            style={{ color: 'var(--text-secondary)' }}
          >
            {showComparison ? 'Hide full comparison' : 'See the full comparison'}
          </button>

          <AnimatePresence initial={false}>
            {showComparison && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: duration.fast, ease: ease.out }}
                className="overflow-hidden"
              >
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-sm border-collapse min-w-[560px]">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                        <th className="text-left py-3 pr-4 font-medium" style={{ color: 'var(--text-secondary)' }}>
                          &nbsp;
                        </th>
                        {growthPlans.map((plan) => (
                          <th
                            key={plan.id}
                            className="text-left py-3 px-4 font-medium whitespace-nowrap"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {plan.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {growthPlanComparison.map((row) => (
                        <tr key={row.label} className="border-b" style={{ borderColor: 'var(--border)' }}>
                          <td className="py-3 pr-4" style={{ color: 'var(--text-secondary)' }}>
                            {row.label}
                          </td>
                          {row.values.map((v, i) => (
                            <td key={i} className="py-3 px-4" style={{ color: 'var(--text-muted)' }}>
                              {typeof v === 'boolean' ? (
                                v ? (
                                  <span aria-label="Included" style={{ color: 'var(--accent)' }}>
                                    ✓
                                  </span>
                                ) : (
                                  <span aria-label="Not included" style={{ color: 'var(--border-strong)' }}>
                                    —
                                  </span>
                                )
                              ) : (
                                v
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-8 text-xs max-w-xl" style={{ color: 'var(--text-muted)' }}>
          Care Plans begin once your website is live, whether it was built by us or
          elsewhere. Every plan is tailored to your goals; the prices above are starting points.
        </p>
      </div>
    </section>
  )
}
